import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Loader2, AlertCircle, CheckCircle2, ArrowLeft, X, Mail, Lock, ChevronDown, Tag } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { GENERATE_AUTH_TOKEN } from '../config';

interface SubscriptionPlan {
  id: string;
  name: string;
  price: number;
  description: string;
  features: string[];
  max_fotos_mes: number;
}

const plans: SubscriptionPlan[] = [
  {
    id: "emprendimiento",
    name: "Básico",
    price: 21999,
    description: "Pensado para pequeños emprendimientos que están buscando profesionalizar su negocio",
    features: [
      "Hasta 60 imágenes por mes",
      "Función de Edición de imágenes con IA",
      "Acceso desde computadora, tablet y celular",
      "~Generación de videos con IA"
    ],
    max_fotos_mes: 60
  },
  {
    id: "negocio",
    name: "Pro",
    price: 69999,
    description: "Ideal para negocios en crecimiento que quieren imágenes de alta calidad para impulsar sus ventas",
    features: [
      "Hasta 250 imágenes por mes",
      "Hasta 20 videos por mes",
      "Función de Edición de imágenes con IA",
      "Acceso desde computadora, tablet y celular",
      "Soporte prioritario"
    ],
    max_fotos_mes: 250
  },
  {
    id: "enterprise",
    name: "Ultra",
    price: 209999,
    description: "Creado para negocios con producción de imágenes y gestión catálogos grandes",
    features: [
      "Hasta 900 imágenes por mes",
      "Hasta 40 videos por mes",
      "Función de Edición de imágenes con IA",
      "Acceso desde computadora, tablet y celular",
      "Soporte dedicado 24/7",
      "Mejor relación precio / imagen",
      "Asesoría estratégica para campañas"
    ],
    max_fotos_mes: 900
  },
  {
    id: "enterprise1",
    name: "Ultra Nivel 1",
    price: 109999,
    description: "Creado para negocios con producción de imágenes y gestión catálogos grandes",
    features: [
      "Hasta 500 imágenes por mes",
      "Hasta 40 videos por mes",
      "Función de Edición de imágenes con IA",
      "Acceso desde computadora, tablet y celular",
      "Soporte dedicado 24/7",
      "Mejor relación precio / imagen",
      "Asesoría estratégica para campañas"
    ],
    max_fotos_mes: 500
  },
  {
    id: "enterprise2",
    name: "Ultra Nivel 2",
    price: 209999,
    description: "Creado para negocios con producción de imágenes y gestión catálogos grandes",
    features: [
      "Hasta 1.000 imágenes por mes",
      "Hasta 80 videos por mes",
      "Función de Edición de imágenes con IA",
      "Acceso desde computadora, tablet y celular",
      "Soporte dedicado 24/7",
      "Mejor relación precio / imagen",
      "Asesoría estratégica para campañas"
    ],
    max_fotos_mes: 1000
  },
  {
    id: "enterprise3",
    name: "Ultra Nivel 3",
    price: 399999,
    description: "Creado para negocios con producción de imágenes y gestión catálogos grandes",
    features: [
      "Hasta 2.000 imágenes por mes",
      "Hasta 160 videos por mes",
      "Función de Edición de imágenes con IA",
      "Acceso desde computadora, tablet y celular",
      "Soporte dedicado 24/7",
      "Mejor relación precio / imagen",
      "Asesoría estratégica para campañas"
    ],
    max_fotos_mes: 2000
  }
];

const isEnterprisePlan = (plan: string) => plan === 'enterprise' || plan === 'enterprise1' || plan === 'enterprise2' || plan === 'enterprise3';

export const Checkout = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const planId = (location.state as any)?.planId as string | undefined;
  const pais = ((location.state as any)?.pais as string) || 'Argentina';
  const initialCoupon = (location.state as any)?.coupon as { id: string; codigo: string; multiplicador: number; es_invisible?: boolean } | undefined;
  const currentPlan = ((location.state as any)?.currentPlan as string) || 'gratis';

  // Plan hierarchy for determining upgrades
  const planOrder: Record<string, number> = { gratis: 0, emprendimiento: 1, negocio: 2, enterprise: 3, enterprise1: 3.1, enterprise2: 3.2, enterprise3: 3.3, experto: 4 };
  const isUpgrade = planId ? planOrder[planId] > (planOrder[currentPlan] ?? 0) : false;

  const normalizedPais = (pais || '').toLowerCase();
  const isArgentina = normalizedPais === 'argentina';
  const isChile = normalizedPais === 'chile';
  const isColombia = normalizedPais === 'colombia';
  const isMexico = normalizedPais === 'méxico' || normalizedPais === 'mexico';
  const isPeru = normalizedPais === 'perú' || normalizedPais === 'peru';

  const currencyCode = isArgentina ? 'ARS' : isChile ? 'CLP' : isColombia ? 'COP' : isMexico ? 'MXN' : isPeru ? 'PEN' : 'USD';
  const providerName = isArgentina ? 'Mercado Pago' : 'Stripe';
  const providerLogoUrl = isArgentina
    ? 'https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system//mercado-pago-logo.png'
    : 'https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/stripe-logotipo.png';
  const providerTermsUrl = isArgentina
    ? 'https://www.mercadopago.com.ar/ayuda/terminos-y-condiciones_299'
    : 'https://stripe.com/legal';

  const [contactEmail, setContactEmail] = useState<string>('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [processing, setProcessing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [showFeatures, setShowFeatures] = useState<boolean>(false);
  const [showMpEmailTutorial, setShowMpEmailTutorial] = useState<boolean>(false);
  const [mpEmailTutorialStep, setMpEmailTutorialStep] = useState<1 | 2>(1);

  // Coupon state
  const [showCouponInput, setShowCouponInput] = useState<boolean>(false);
  const [couponCode, setCouponCode] = useState<string>('');
  const [couponLoading, setCouponLoading] = useState<boolean>(false);
  const [couponError, setCouponError] = useState<string | null>(null);
  const [appliedCoupon, setAppliedCoupon] = useState<{ id: string; codigo: string; multiplicador: number; es_invisible?: boolean } | null>(initialCoupon || null);

  // Auto-load Hot Sale coupon if no coupon was passed from Paywall (Argentina only)
  useEffect(() => {
    if (appliedCoupon || !isArgentina) return;
    const loadHotSaleCoupon = async () => {
      try {
        const { data, error: dbError } = await supabase
          .from('cupones')
          .select('id, codigo, multiplicador, activo, usos_maximos, usos_actuales, fecha_expiracion, es_invisible')
          .eq('id', '9e0abd0d-5e0a-4f84-8036-b425f7176207')
          .single();
        if (!dbError && data && data.activo) {
          const expired = data.fecha_expiracion && new Date(data.fecha_expiracion) < new Date();
          const maxedOut = data.usos_maximos !== null && data.usos_actuales >= data.usos_maximos;
          if (!expired && !maxedOut) {
            setAppliedCoupon({ id: data.id, codigo: data.codigo, multiplicador: data.multiplicador, es_invisible: true });
          }
        }
      } catch {}
    };
    loadHotSaleCoupon();
  }, [isArgentina]);

  const selectedPlan = plans.find(p => p.id === planId);

  const getPlanPrice = (id: string, arsPrice: number) => {
    if (isArgentina) return arsPrice;
    if (isChile) {
      switch (id) { case 'emprendimiento': return 14999; case 'negocio': return 49999; case 'enterprise': case 'enterprise1': return 79999; case 'enterprise2': return 149999; case 'enterprise3': return 299999; default: return 0; }
    }
    if (isColombia) {
      switch (id) { case 'emprendimiento': return 59999; case 'negocio': return 199000; case 'enterprise': case 'enterprise1': return 299999; case 'enterprise2': return 549999; case 'enterprise3': return 1099999; default: return 0; }
    }
    if (isMexico) {
      switch (id) { case 'emprendimiento': return 299; case 'negocio': return 999; case 'enterprise': case 'enterprise1': return 1699; case 'enterprise2': return 2999; case 'enterprise3': return 5999; default: return 0; }
    }
    if (isPeru) {
      switch (id) { case 'emprendimiento': return 49; case 'negocio': return 179; case 'enterprise': case 'enterprise1': return 299; case 'enterprise2': return 549; case 'enterprise3': return 1099; default: return 0; }
    }
    switch (id) { case 'emprendimiento': return 14; case 'negocio': return 49; case 'enterprise': case 'enterprise1': return 79; case 'enterprise2': return 149; case 'enterprise3': return 299; default: return 0; }
  };

  const formatPrice = (amount: number) => {
    const locale = isArgentina ? 'es-AR' : isChile ? 'es-CL' : isColombia ? 'es-CO' : isMexico ? 'es-MX' : isPeru ? 'es-PE' : 'en-US';
    const fmt = new Intl.NumberFormat(locale, { minimumFractionDigits: 0 });
    return `$${fmt.format(amount)} ${currencyCode}`;
  };

  // Redirect back if no plan selected
  useEffect(() => {
    if (!planId || !selectedPlan) {
      navigate('/subscription', { replace: true });
    }
  }, [planId, selectedPlan, navigate]);

  // Prefill email from previous checkout (Argentina / Mercado Pago)
  useEffect(() => {
    if (!isArgentina) return;
    (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        const { data: prof } = await supabase.from('profiles').select('id_company').eq('id', user.id).single();
        if (!prof?.id_company) return;
        const { data: company } = await supabase.from('company_profiles').select('email_contacto').eq('id_company', prof.id_company).single();
        if (company?.email_contacto) setContactEmail(company.email_contacto);
      } catch {}
    })();
  }, [isArgentina]);

  // When user returns from payment provider (browser back), check if plan changed → redirect to success
  useEffect(() => {
    const handleVisibility = async () => {
      if (document.visibilityState !== 'visible') return;
      // Only check if we were in a processing state (user was redirected to payment)
      if (!processing) return;
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        const { data: prof } = await supabase.from('profiles').select('id_company').eq('id', user.id).single();
        if (!prof?.id_company) return;
        const { data: company } = await supabase.from('company_profiles').select('plan').eq('id_company', prof.id_company).single();
        if (company?.plan && company.plan === planId) {
          // User successfully subscribed to the plan they were buying
          navigate('/subscription/success', { replace: true });
          return;
        }
      } catch {}
      // If plan didn't change, just reset processing so user isn't stuck on spinner
      setProcessing(false);
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [processing, planId, navigate]);

  // Also handle pageshow event (bfcache restoration on mobile Safari)
  useEffect(() => {
    const handlePageShow = async (e: PageTransitionEvent) => {
      if (!e.persisted) return;
      if (!processing) return;
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        const { data: prof } = await supabase.from('profiles').select('id_company').eq('id', user.id).single();
        if (!prof?.id_company) return;
        const { data: company } = await supabase.from('company_profiles').select('plan').eq('id_company', prof.id_company).single();
        if (company?.plan && company.plan === planId) {
          navigate('/subscription/success', { replace: true });
          return;
        }
      } catch {}
      setProcessing(false);
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, [processing, planId, navigate]);

  // Fire-and-forget: notify backend that user reached checkout
  const checkoutNotifiedRef = useRef(false);
  useEffect(() => {
    if (checkoutNotifiedRef.current) return;
    checkoutNotifiedRef.current = true;
    (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          fetch('https://ai-agency-dt7d.onrender.com/webhook/paso_por_checkout', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': GENERATE_AUTH_TOKEN,
            },
            body: JSON.stringify({ user_id: user.id }),
          }).catch(() => {});
        }
      } catch {}
    })();
  }, []);

  const validateEmail = (email: string): boolean => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleValidateCoupon = async () => {
    const code = couponCode.trim();
    if (!code) { setCouponError('Ingresá un código de cupón'); return; }
    try {
      setCouponLoading(true);
      setCouponError(null);
      const { data, error: dbError } = await supabase
        .from('cupones')
        .select('id, codigo, multiplicador, activo, usos_maximos, usos_actuales, fecha_expiracion, es_invisible')
        .eq('codigo', code)
        .single();
      if (dbError || !data) { setCouponError('Cupón no encontrado'); return; }
      if (!data.activo) { setCouponError('Este cupón ya no está activo'); return; }
      if (data.usos_maximos !== null && data.usos_actuales >= data.usos_maximos) { setCouponError('Este cupón ya alcanzó su límite de usos'); return; }
      if (data.fecha_expiracion && new Date(data.fecha_expiracion) < new Date()) { setCouponError('Este cupón ha expirado'); return; }
      setAppliedCoupon({ id: data.id, codigo: data.codigo, multiplicador: data.multiplicador, es_invisible: !!data.es_invisible });
      setShowCouponInput(false);
      setCouponCode('');
    } catch {
      setCouponError('Error al validar el cupón');
    } finally {
      setCouponLoading(false);
    }
  };

  const handleCheckout = async () => {
    setEmailError(null);
    setError(null);

    // Only validate email for Argentina (Mercado Pago)
    if (isArgentina) {
      if (!contactEmail.trim()) {
        setEmailError('El correo electrónico es obligatorio');
        return;
      }

      if (!validateEmail(contactEmail)) {
        setEmailError('Ingresá un correo electrónico válido');
        return;
      }
    }

    try {
      setProcessing(true);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuario no encontrado');

      // For Argentina, save email first
      if (isArgentina) {
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('id_company')
          .eq('id', user.id)
          .single();

        if (profileError) throw profileError;
        if (!profileData.id_company) throw new Error('No hay una empresa asociada a este usuario');

        const { error: updateError } = await supabase
          .from('company_profiles')
          .update({ email_contacto: contactEmail })
          .eq('id_company', profileData.id_company);

        if (updateError) throw updateError;
      }

      // Redirect to the correct provider
      const endpoint = isArgentina
        ? 'https://ai-agency-dt7d.onrender.com/webhook/08741f54-6bda-4751-8054-14a59f8338ca'
        : 'https://ai-agency-dt7d.onrender.com/webhook/stripe-start';
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': GENERATE_AUTH_TOKEN,
        },
        body: JSON.stringify({
          user_id: user.id,
          plan: planId,
          ...(appliedCoupon && isUpgrade ? { con_descuento: true, cupon_id: appliedCoupon.id } : {})
        }),
      });

      if (!response.ok) {
        throw new Error('Error al obtener el enlace de checkout');
      }

      const data = await response.json();

      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No se recibió una URL válida para el checkout');
      }
    } catch (err: any) {
      console.error('Error in checkout:', err);
      setError(err.message || 'Error al procesar el pago');
      setProcessing(false);
    }
  };

  if (!selectedPlan) return null;

  const price = getPlanPrice(selectedPlan.id, selectedPlan.price);
  const hasDiscount = appliedCoupon && (selectedPlan.id === 'negocio' || isEnterprisePlan(selectedPlan.id)) && isUpgrade;
  const hasFixedDiscount = !isArgentina && !appliedCoupon && (selectedPlan.id === 'negocio' || isEnterprisePlan(selectedPlan.id)) && isUpgrade;
  const discountedPrice = hasDiscount ? Math.floor(price * appliedCoupon.multiplicador) : hasFixedDiscount ? Math.floor(price * 0.5) : price;

  return (
    <div className="min-h-screen bg-surface-card md:bg-surface-bg">

      {/* ═══════════ MOBILE LAYOUT (Stripe-style) ═══════════ */}
      <div className="md:hidden flex flex-col min-h-screen pb-[140px]">
        {/* Mobile header */}
        <div className="px-5 pt-5 pb-2 flex items-center justify-between">
          <button
            onClick={() => navigate('/subscription', { replace: true })}
            className="text-[13px] text-neutral-500 hover:text-neutral-700 flex items-center gap-1 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver
          </button>
          <div className="flex items-center gap-1 text-[11px] text-neutral-400">
            <Lock className="w-3 h-3" />
            <span>Pago seguro</span>
          </div>
        </div>

        {error && (
          <div className="mx-5 mb-3 p-3 bg-red-50 border border-red-200 rounded-md flex items-center gap-2.5 text-[15px] text-red-700">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Plan summary */}
        <div className="px-5 pt-2 pb-5 border-b border-neutral-200 text-center">
          <p className="text-[13px] text-neutral-500">Plan {selectedPlan.name}</p>
          {(hasDiscount || hasFixedDiscount) ? (
            <div className="mt-1">
              <p className="text-[17px] font-bold text-brand-black/90 tracking-tight leading-tight">
                {formatPrice(discountedPrice)}
                <span className="text-[13px] font-normal text-neutral-500 ml-1">el primer mes</span>
              </p>
              <p className="text-[13px] text-neutral-400 mt-0.5">
                Después {formatPrice(price)} por mes
              </p>
            </div>
          ) : (
            <p className="text-[17px] font-bold text-brand-black/90 tracking-tight leading-tight mt-1">
              {formatPrice(price)}
              <span className="text-[13px] font-normal text-neutral-400 ml-1">/ mes</span>
            </p>
          )}

          {/* Expandable features */}
          <button
            type="button"
            onClick={() => setShowFeatures(!showFeatures)}
            className="mt-3 inline-flex items-center gap-1 text-[13px] text-brand-primary hover:text-brand-primary-hover font-medium transition-colors"
          >
            Ver qué incluye
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showFeatures ? 'rotate-180' : ''}`} />
          </button>

          {showFeatures && (
            <ul className="mt-3 space-y-2 text-left">
              {selectedPlan.features.map((feature, i) => (
                <li key={i} className="flex items-center text-[13px] text-neutral-600">
                  <CheckCircle2 className="w-3.5 h-3.5 text-brand-primary mr-2 flex-shrink-0" />
                  {feature}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Payment section */}
        <div className="px-5 pt-5 space-y-5">
          {/* Provider method */}
          <div>
            <p className="text-[13px] font-medium text-brand-black/90 mb-1.5">Método de pago</p>
            <div className="flex items-center justify-center gap-2.5 p-3 rounded-md bg-brand-primary/5 border border-brand-primary/20">
              <img src={providerLogoUrl} alt={providerName} className="h-4 w-auto" />
              {isArgentina && <span className="text-[13px] font-semibold text-brand-primary">{providerName}</span>}
            </div>
          </div>

          {/* Email — only for Argentina (Mercado Pago) */}
          {isArgentina && (
          <div>
            <label htmlFor="checkout-email-mobile" className="block text-[13px] font-medium text-brand-black/90 mb-1.5">
              Email de tu cuenta de Mercado Pago
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Mail className="h-4 w-4 text-neutral-400" />
              </div>
              <input
                type="email"
                id="checkout-email-mobile"
                className="bg-surface-card block w-full pl-10 text-[15px] border border-neutral-200 rounded-md h-10 px-3 focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary transition-colors"
                placeholder="email@mercadopago.com"
                value={contactEmail}
                onChange={(e) => { setContactEmail(e.target.value); setEmailError(null); }}
                onKeyDown={(e) => { if (e.key === 'Enter') handleCheckout(); }}
              />
            </div>
            {emailError && (
              <p className="mt-1.5 text-[13px] text-red-600">{emailError}</p>
            )}

            <div className="mt-3 bg-surface-elevated border border-neutral-200 rounded-md p-3 flex gap-2 items-start">
              <AlertCircle className="w-4 h-4 text-neutral-400 mt-0.5 flex-shrink-0" />
              <p className="text-[11px] text-neutral-500">
                <span className="font-semibold text-brand-black/90">Usá el mismo email que tenés en Mercado Pago</span> para que podamos asociar el pago y activar tu estudio automáticamente.
              </p>
            </div>

            <button
              type="button"
              onClick={() => { setMpEmailTutorialStep(1); setShowMpEmailTutorial(true); }}
              className="text-[11px] text-brand-primary hover:underline mt-2 block"
            >
              ¿Cómo saber mi correo de Mercado Pago?
            </button>
          </div>
          )}

          {/* Stripe info — only for non-Argentina */}
          {!isArgentina && (
            <div className="mt-1">
              <p className="text-[13px] text-neutral-500">
                Al hacer clic en "Activar mi Estudio Atlas", serás redirigido a Stripe para completar tu suscripción de manera segura.
              </p>
            </div>
          )}

          {/* Coupon section — mobile (Argentina only) */}
          {isArgentina && <div className="mt-4">
            {appliedCoupon && appliedCoupon.es_invisible ? null : appliedCoupon ? (
              <div className="flex items-center justify-between p-3 bg-brand-primary/5 border border-brand-primary/20 rounded-md">
                <div className="flex items-center gap-2">
                  <Tag className="w-4 h-4 text-brand-primary" />
                  <span className="text-[13px] text-brand-black/90">
                    Cupón <strong>{appliedCoupon.codigo}</strong> — {Math.round((1 - appliedCoupon.multiplicador) * 100)}% OFF
                  </span>
                </div>
                <button type="button" onClick={() => setAppliedCoupon(null)} className="text-[13px] text-neutral-500 hover:text-neutral-700 font-medium transition-colors">Quitar</button>
              </div>
            ) : showCouponInput ? (
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={couponCode}
                    onChange={(e) => { setCouponCode(e.target.value.toUpperCase()); setCouponError(null); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleValidateCoupon(); }}
                    placeholder="Código de cupón"
                    className="flex-1 h-10 px-3 text-[15px] border border-neutral-200 rounded-md bg-surface-card focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary transition-colors"
                    autoFocus
                  />
                  <button type="button" onClick={handleValidateCoupon} disabled={couponLoading} className="h-10 px-4 bg-brand-primary hover:bg-brand-primary-hover text-white text-[13px] font-medium rounded-md shadow-sm transition-colors flex items-center gap-1.5">
                    {couponLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Aplicar'}
                  </button>
                  <button type="button" onClick={() => { setShowCouponInput(false); setCouponCode(''); setCouponError(null); }} className="text-[13px] text-neutral-500 hover:text-neutral-700 transition-colors">Cancelar</button>
                </div>
                {couponError && (
                  <p className="text-[13px] text-red-600 flex items-center gap-1.5"><AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />{couponError}</p>
                )}
              </div>
            ) : (
              <button type="button" onClick={() => setShowCouponInput(true)} className="flex items-center gap-1.5 text-[13px] text-brand-primary hover:text-brand-primary-hover font-medium transition-colors">
                <Tag className="w-3.5 h-3.5" />Tengo un cupón
              </button>
            )}
          </div>}
        </div>
      </div>

      {/* Mobile sticky footer */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-surface-card border-t border-neutral-200 px-5 py-3 z-40" style={{ boxShadow: '0 -4px 20px rgba(15, 23, 42, 0.06)' }}>
        <button
          onClick={handleCheckout}
          disabled={processing}
          className="w-full h-10 bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-medium rounded-md shadow-sm flex items-center justify-center transition-colors"
        >
          {processing ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            'Activar mi Estudio Atlas'
          )}
        </button>
        <p className="text-[11px] text-neutral-400 text-center mt-2">
          Al continuar aceptás los{' '}
          <a href={providerTermsUrl} target="_blank" rel="noopener noreferrer" className="text-brand-primary hover:underline">términos</a>{' '}
          de {providerName}
        </p>
      </div>

      {/* ═══════════ DESKTOP LAYOUT ═══════════ */}
      <div className="hidden md:block">
        <div className="max-w-[1280px] mx-auto px-6 py-12">
          {/* Header — inline above cards */}
          <div className="flex items-center justify-between mb-10">
            <div>
              <h3 className="text-[17px] font-semibold text-brand-black/90 mb-1">Estudio Atlas</h3>
              <button
                onClick={() => navigate('/subscription', { replace: true })}
                className="text-[13px] text-neutral-500 hover:text-neutral-700 flex items-center gap-1 transition-colors"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                Volver
              </button>
            </div>
            <div className="flex items-center gap-1.5 text-[13px] text-neutral-500">
              <Lock className="w-3.5 h-3.5" />
              <span>Pago 100% seguro · Cancelás cuando quieras</span>
            </div>
          </div>

          {error && (
            <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-md flex items-center gap-2.5 text-[15px] text-red-700">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="grid grid-cols-2 gap-8">
            {/* Left: Plan summary */}
            <div className="bg-surface-card rounded-lg border border-neutral-200 p-8">
              <h1 className="text-[17px] font-semibold text-brand-black/90 mb-2">
                Estás a un paso de crear con Estudio Atlas
              </h1>
              <p className="text-[13px] text-neutral-500 mb-6">
                Activá tu estudio y empezá a generar fotos profesionales con IA.
              </p>

              <div className="border border-neutral-200 rounded-md p-5 bg-surface-elevated">
                <h3 className="text-[17px] font-bold text-brand-black/90 uppercase">Plan {selectedPlan.name}</h3>
                <p className="text-[13px] text-neutral-500 mt-1">{selectedPlan.description}</p>
                {(hasDiscount || hasFixedDiscount) ? (
                  <>
                    <p className="text-[17px] font-bold text-brand-black/90 mt-4">
                      {formatPrice(discountedPrice)}
                      <span className="text-[13px] font-normal text-neutral-500 ml-1">el primer mes</span>
                    </p>
                    <p className="text-[13px] text-neutral-400 mt-1">
                      Después {formatPrice(price)} por mes
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-[17px] font-bold text-brand-black/90 mt-4">
                      {formatPrice(price)}
                    </p>
                    <p className="text-[13px] text-neutral-500 -mt-0.5">/ mes</p>
                  </>
                )}
                <ul className="mt-5 space-y-2.5">
                  {selectedPlan.features.map((feature, i) => (
                    <li key={i} className="flex items-center text-[13px] text-neutral-600">
                      <CheckCircle2 className="w-4 h-4 text-brand-primary mr-2.5 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>

              <p className="text-[11px] text-neutral-400 mt-5">
                <span className="font-medium text-neutral-600">Sin permanencia.</span> Podés cambiar o cancelar tu plan cuando quieras.
              </p>
            </div>

            {/* Right: Payment form */}
            <div className="bg-surface-card rounded-lg border border-neutral-200 p-8 flex flex-col">
              <h2 className="text-[17px] font-semibold text-brand-black/90 mb-1">Datos de pago</h2>
              <p className="text-[13px] text-neutral-500 mb-6">Activás tu estudio en minutos.</p>

              {/* Email — only for Argentina (Mercado Pago) */}
              {isArgentina && (
              <div>
                <label htmlFor="checkout-email-desktop" className="block text-[13px] font-medium text-brand-black/90 mb-1.5">
                  Email (debe ser el mismo que usás en Mercado Pago)
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-4 w-4 text-neutral-400" />
                  </div>
                  <input
                    type="email"
                    id="checkout-email-desktop"
                    className="bg-surface-card block w-full pl-10 text-[15px] border border-neutral-200 rounded-md h-10 px-3 focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary transition-colors"
                    placeholder="email@mercadopago.com"
                    value={contactEmail}
                    onChange={(e) => { setContactEmail(e.target.value); setEmailError(null); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleCheckout(); }}
                  />
                </div>
                {emailError && (
                  <p className="mt-1.5 text-[13px] text-red-600">{emailError}</p>
                )}

                <div className="mt-3 bg-surface-elevated border border-neutral-200 rounded-md p-3 flex gap-2 items-start">
                  <AlertCircle className="w-4 h-4 text-neutral-400 mt-0.5 flex-shrink-0" />
                  <p className="text-[11px] text-neutral-500">
                    <span className="font-semibold text-brand-black/90">Usá el mismo email que tenés en Mercado Pago</span> para que podamos asociar el pago y activar tu estudio automáticamente.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => { setMpEmailTutorialStep(1); setShowMpEmailTutorial(true); }}
                  className="text-[11px] text-brand-primary hover:underline mt-2 block"
                >
                  ¿Cómo saber el correo de mi cuenta de Mercado Pago?
                </button>
              </div>
              )}

              <div className={isArgentina ? "mt-6" : ""}>
                <label className="block text-[13px] font-medium text-brand-black/90 mb-1.5">Método de pago</label>
                <div className="flex items-center justify-center gap-2.5 p-3 rounded-md bg-brand-primary/5 border border-brand-primary/20">
                  <img src={providerLogoUrl} alt={providerName} className="h-5 w-auto" />
                  {isArgentina && <span className="text-[13px] font-semibold text-brand-primary">{providerName}</span>}
                </div>
              </div>

              {/* Stripe info — only for non-Argentina */}
              {!isArgentina && (
                <div className="mt-4">
                  <p className="text-[13px] text-neutral-500">
                    Al hacer clic en "Activar mi Estudio Atlas", serás redirigido a Stripe para completar tu suscripción de manera segura.
                  </p>
                </div>
              )}

              {/* Coupon section — desktop (Argentina only) */}
              {isArgentina && <div className="mt-5">
                {appliedCoupon && appliedCoupon.es_invisible ? null : appliedCoupon ? (
                  <div className="flex items-center justify-between p-3 bg-brand-primary/5 border border-brand-primary/20 rounded-md">
                    <div className="flex items-center gap-2">
                      <Tag className="w-4 h-4 text-brand-primary" />
                      <span className="text-[13px] text-brand-black/90">
                        Cupón <strong>{appliedCoupon.codigo}</strong> — {Math.round((1 - appliedCoupon.multiplicador) * 100)}% OFF
                      </span>
                    </div>
                    <button type="button" onClick={() => setAppliedCoupon(null)} className="text-[13px] text-neutral-500 hover:text-neutral-700 font-medium transition-colors">Quitar</button>
                  </div>
                ) : showCouponInput ? (
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={couponCode}
                        onChange={(e) => { setCouponCode(e.target.value.toUpperCase()); setCouponError(null); }}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleValidateCoupon(); }}
                        placeholder="Código de cupón"
                        className="flex-1 h-10 px-3 text-[15px] border border-neutral-200 rounded-md bg-surface-card focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary transition-colors"
                        autoFocus
                      />
                      <button type="button" onClick={handleValidateCoupon} disabled={couponLoading} className="h-10 px-4 bg-brand-primary hover:bg-brand-primary-hover text-white text-[13px] font-medium rounded-md shadow-sm transition-colors flex items-center gap-1.5">
                        {couponLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Aplicar'}
                      </button>
                      <button type="button" onClick={() => { setShowCouponInput(false); setCouponCode(''); setCouponError(null); }} className="text-[13px] text-neutral-500 hover:text-neutral-700 transition-colors">Cancelar</button>
                    </div>
                    {couponError && (
                      <p className="text-[13px] text-red-600 flex items-center gap-1.5"><AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />{couponError}</p>
                    )}
                  </div>
                ) : (
                  <button type="button" onClick={() => setShowCouponInput(true)} className="flex items-center gap-1.5 text-[13px] text-brand-primary hover:text-brand-primary-hover font-medium transition-colors">
                    <Tag className="w-3.5 h-3.5" />Tengo un cupón
                  </button>
                )}
              </div>}

              {/* Bottom section — pushed down */}
              <div className="mt-auto pt-6">
                <div className="flex items-center justify-between text-[17px] font-semibold text-brand-black/90">
                  <span>Total</span>
                  <span>{formatPrice(discountedPrice)}</span>
                </div>
                {(hasDiscount || hasFixedDiscount) && (
                  <p className="text-[13px] text-neutral-400 mt-0.5 text-right">Después {formatPrice(price)} por mes</p>
                )}
                <p className="text-[11px] text-neutral-400 mt-1">Incluye impuestos · Facturación automática</p>

                <button
                  onClick={handleCheckout}
                  disabled={processing}
                  className="w-full mt-5 h-10 px-4 bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-medium rounded-md shadow-sm flex items-center justify-center transition-colors"
                >
                  {processing ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    'Activar mi Estudio Atlas'
                  )}
                </button>

                <p className="text-[11px] text-neutral-400 text-center mt-3">
                  Al continuar aceptás los{' '}
                  <a href={providerTermsUrl} target="_blank" rel="noopener noreferrer" className="text-brand-primary hover:underline">
                    términos y condiciones
                  </a>{' '}
                  de {providerName}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mercado Pago Email Tutorial Modal */}
      {showMpEmailTutorial && (
        <div className="fixed inset-0 bg-brand-black/60 backdrop-blur-sm flex items-center justify-center z-[60] px-6 py-4">
          <div className="bg-surface-card rounded-lg shadow-md max-w-sm w-full p-5 relative">
            <button
              onClick={() => setShowMpEmailTutorial(false)}
              className="absolute top-4 right-4 text-neutral-400 hover:text-neutral-600 transition-colors"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>

            {mpEmailTutorialStep === 1 ? (
              <>
                <div className="mb-4">
                  <img
                    src="https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/mp_como_ver_mi_mail.png"
                    alt="Paso 1: Pantalla de inicio de Mercado Pago"
                    className="w-full max-h-96 object-contain rounded-md"
                  />
                </div>
                <p className="text-[13px] text-neutral-500 mb-4">
                  Para conocer qué correo tienes asociado a tu cuenta de Mercado Pago, entra a la aplicación y en la pantalla de inicio haz click en la esquina superior izquierda.
                </p>
                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => setMpEmailTutorialStep(2)}
                    className="w-full h-10 px-4 bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-medium rounded-md shadow-sm transition-colors"
                  >
                    Siguiente
                  </button>
                  <button
                    onClick={() => setShowMpEmailTutorial(false)}
                    className="w-full h-10 px-4 bg-surface-elevated border border-neutral-200 text-brand-black/90 text-[15px] font-medium rounded-md hover:bg-neutral-100 transition-colors"
                  >
                    Cerrar
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="mb-4">
                  <img
                    src="https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/mp_como_ver_mi_mail_2.png"
                    alt="Paso 2: Ver tu email en Mercado Pago"
                    className="w-full max-h-96 object-contain rounded-md"
                  />
                </div>
                <p className="text-[13px] text-neutral-500 mb-4">
                  En esta pantalla podrás ver el correo electrónico asociado a tu cuenta de Mercado Pago. Usa ese mismo correo al suscribirte.
                </p>
                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => setShowMpEmailTutorial(false)}
                    className="w-full h-10 px-4 bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-medium rounded-md shadow-sm transition-colors"
                  >
                    Entendido
                  </button>
                  <button
                    onClick={() => setMpEmailTutorialStep(1)}
                    className="w-full h-10 px-4 bg-surface-elevated border border-neutral-200 text-brand-black/90 text-[15px] font-medium rounded-md hover:bg-neutral-100 transition-colors"
                  >
                    Volver
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Checkout;
